import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import type { ModelDistributionRow } from '../../api/types';

interface Props {
  models: ModelDistributionRow[];
  loading?: boolean;
}

const MODEL_FAMILY_COLORS: Array<{ match: RegExp; color: string }> = [
  { match: /sonnet/i, color: '#6366f1' }, // indigo
  { match: /opus/i, color: '#f59e0b' }, // amber
  { match: /haiku/i, color: '#10b981' }, // emerald
];
const FALLBACK_COLORS = ['#ec4899', '#0ea5e9', '#8b5cf6', '#84cc16', '#f97316'];

function colorFor(model: string): string {
  for (const { match, color } of MODEL_FAMILY_COLORS) {
    if (match.test(model)) return color;
  }
  let hash = 0;
  for (let i = 0; i < model.length; i++) hash = (hash * 31 + model.charCodeAt(i)) | 0;
  return FALLBACK_COLORS[Math.abs(hash) % FALLBACK_COLORS.length] ?? '#6366f1';
}

function formatTokens(value: number): string {
  if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B`;
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
  return String(value);
}

export function ModelDistributionDonut({ models, loading }: Props): JSX.Element {
  if (!loading && models.length === 0) {
    return <div className="model-distribution-empty">No model usage in this window.</div>;
  }

  const total = models.reduce((s, m) => s + m.tokens, 0);

  const option: EChartsOption = {
    tooltip: {
      trigger: 'item',
      formatter: (params) => {
        const p = Array.isArray(params) ? params[0] : params;
        if (!p) return '';
        const value = p.value as number;
        const pct = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
        return `<strong>${p.name}</strong><br/>${formatTokens(value)} tokens (${pct}%)`;
      },
    },
    legend: {
      bottom: 0,
      icon: 'circle',
      itemGap: 16,
    },
    series: [
      {
        name: 'Tokens by model',
        type: 'pie',
        radius: ['52%', '78%'],
        center: ['50%', '44%'],
        avoidLabelOverlap: true,
        itemStyle: {
          borderColor: '#fff',
          borderWidth: 2,
        },
        label: {
          show: true,
          position: 'outside',
          formatter: (p) => {
            const value = p.value as number;
            if (total === 0) return '';
            const pct = (value / total) * 100;
            return pct >= 5 ? `${p.name}\n${pct.toFixed(0)}%` : '';
          },
          fontSize: 12,
          color: 'inherit',
        },
        labelLine: { show: true, length: 8, length2: 6 },
        data: models.map((m) => ({
          name: m.model,
          value: m.tokens,
          itemStyle: { color: colorFor(m.model) },
        })),
      },
    ],
    graphic: [
      {
        type: 'text',
        left: 'center',
        top: '38%',
        style: {
          text: formatTokens(total),
          align: 'center',
          fontSize: 22,
          fontWeight: 700,
          fill: '#111',
        },
      },
      {
        type: 'text',
        left: 'center',
        top: '49%',
        style: {
          text: 'tokens',
          align: 'center',
          fontSize: 11,
          fill: '#888',
        },
      },
    ],
  };

  return (
    <ReactECharts
      option={option}
      style={{ height: 408, width: '100%' }}
      showLoading={loading}
      notMerge
      lazyUpdate
    />
  );
}
